/**
 * AWDD Theme - Main JavaScript
 * Uses GSAP + ScrollTrigger for animations
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import initHeader from './header.js';
import initJumpLinks from './jump-links.js';
import initDialogs from './dialog.js';
import { initCouncil } from './council.js';
import initOutbound from './outbound.js';
import initLookups from './lookup.js';
import initHeaderCouncil from './header-council.js';
import initCouncilLinks from './council-link.js';
import initTranslate from './translate.js';
import initSteps from './steps.js';
import initTerms from './terms.js';
import initFaq from './faq.js';
import initEvents from './events.js';
import initContact from './contact.js';
import initSuppliers from './suppliers.js';
import initTips from './tips.js';
import initZoho from './zoho.js';

gsap.registerPlugin(ScrollTrigger);

// Disarms awdd_reveal_gate()'s deadline (functions.php).
window.awddRevealReady = true;

document.addEventListener('DOMContentLoaded', function () {

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // The council first: landing on a council page sets it before anything reads it.
    initCouncil();
    initHeader();
    initJumpLinks();
    initDialogs();
    initOutbound();
    initLookups();
    initHeaderCouncil();
    initCouncilLinks();
    initTranslate();
    initSteps();
    initTerms();
    initFaq();
    initEvents();
    initContact();
    initSuppliers();
    initTips();
    initZoho();

    // ===== Scroll reveals =====
    // Motion comes from theme.css, so the reveal and the CSS agree. GSAP cannot
    // parse a cubic-bezier string without CustomEase; expo.out is the closest
    // built-in to the house curve (0.16, 1, 0.3, 1).
    const rootStyle = getComputedStyle(document.documentElement);
    const msVar = (name, fallback) => {
        const raw = rootStyle.getPropertyValue(name).trim();
        const v = parseFloat(raw);
        if (!Number.isFinite(v)) return fallback;
        // The CSS minifier may rewrite 400ms as .4s, so read the unit.
        return raw.endsWith('ms') ? v / 1000 : v;
    };
    const pxVar = (name, fallback) => {
        const v = parseFloat(rootStyle.getPropertyValue(name));
        return Number.isFinite(v) ? v : fallback;
    };
    const revealDuration = msVar('--reveal-ms', 0.4);
    const revealStagger = msVar('--reveal-stagger', 0.07);
    const revealRise = pxVar('--reveal-rise', 8);

    document.querySelectorAll('.reveal').forEach(el => {
        // autoAlpha drives opacity and visibility together; reduced motion skips the tween.
        if (prefersReducedMotion) {
            gsap.set(el, { autoAlpha: 1, y: 0 });
            return;
        }

        const delayMatch = el.className.match(/reveal-delay-(\d+)/);
        const delay = delayMatch ? parseInt(delayMatch[1], 10) * revealStagger : 0;

        gsap.fromTo(el,
            { autoAlpha: 0, y: revealRise },
            {
                autoAlpha: 1, y: 0, duration: revealDuration, ease: 'expo.out',
                delay,
                scrollTrigger: { trigger: el, start: 'top 90%', toggleActions: 'play none none none' }
            }
        );
    });

    // Recalculate after webfonts and images settle, or a layout shift can leave
    // below-the-fold reveals hidden.
    window.addEventListener('load', () => ScrollTrigger.refresh());
    if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => ScrollTrigger.refresh());
    }
    // Safety net for anything ScrollTrigger never fired for. The fade is a
    // nicety, not a gate.
    window.addEventListener('load', () => {
        setTimeout(() => {
            document.querySelectorAll('.reveal').forEach(el => {
                const cs = getComputedStyle(el);
                if (cs.visibility === 'hidden') {
                    gsap.set(el, { autoAlpha: 1, y: 0 });
                }
            });
        }, 2000);
    });

    // ===== Accordions =====
    // Generic helper with no current consumer - ship the [data-awdd-accordion]
    // markup and this runs unchanged. Panels animate by max-height, not display,
    // so the transition can run.
    document.querySelectorAll('[data-awdd-accordion]').forEach((accordion, accIndex) => {
        const toggle = accordion.querySelector('button');
        const panel = accordion.querySelector('[data-awdd-accordion-panel]');
        if (!toggle || !panel) return;

        // The panel is markup-only, so the id aria-controls needs is minted here.
        if (!panel.id) panel.id = `awdd-accordion-panel-${accIndex + 1}`;
        toggle.setAttribute('aria-controls', panel.id);

        panel.style.overflow = 'hidden';
        panel.style.maxHeight = '0px';
        panel.style.opacity = '0';
        if (!prefersReducedMotion) {
            panel.style.transition = 'max-height 0.4s ease, opacity 0.3s ease';
        }
        toggle.setAttribute('aria-expanded', 'false');
        // Clipping to zero height leaves the links in the tab order.
        panel.inert = true;

        toggle.addEventListener('click', () => {
            const isOpen = toggle.getAttribute('aria-expanded') === 'true';
            const nextOpen = !isOpen;

            toggle.setAttribute('aria-expanded', String(nextOpen));
            accordion.classList.toggle('is-open', nextOpen);
            panel.style.maxHeight = nextOpen ? panel.scrollHeight + 'px' : '0px';
            panel.style.opacity = nextOpen ? '1' : '0';
            panel.inert = !nextOpen;
        });

        // Keep an open panel's height correct if content reflows.
        window.addEventListener('resize', () => {
            if (toggle.getAttribute('aria-expanded') === 'true') {
                panel.style.maxHeight = panel.scrollHeight + 'px';
            }
        }, { passive: true });
    });

    // ===== Smooth scroll =====
    // Not scrollIntoView: it puts the target's top at viewport 0, behind the
    // sticky bar, hiding the heading the reader just jumped to.
    document.querySelectorAll('a[href^="#"]').forEach(a => {
        a.addEventListener('click', function (e) {
            const href = this.getAttribute('href');
            if (href === '#') return;

            const t = document.querySelector(href);
            if (!t) return;

            e.preventDefault();

            const bar = document.querySelector('[data-awdd-header]');
            // Measured at click time, not read from the token: the token is a
            // rounded-up budget, and the bar tracks whatever logo is set. The 24
            // matches the scroll padding the stylesheet adds beyond the header and
            // the jump links bar.
            const subnav = document.querySelector('[data-awdd-jump-links]');
            const offset = (bar ? bar.getBoundingClientRect().height : 0)
                + (subnav ? subnav.getBoundingClientRect().height : 0) + 24;

            window.scrollTo({
                top: Math.max(0, t.getBoundingClientRect().top + window.scrollY - offset),
                behavior: 'smooth'
            });

            // Native fragment navigation moves focus to the target; the
            // preventDefault above took that away, so put it back.
            if (!t.matches('a[href], button, input, select, textarea, [tabindex]')) {
                t.setAttribute('tabindex', '-1');
            }
            t.focus({ preventScroll: true });
        });
    });

});
